import { useCallback, useEffect, useState } from 'react'
import { Capacitor } from '@capacitor/core'
import { Preferences } from '@capacitor/preferences'
import {
  BiometricAuth,
  BiometryError,
  BiometryErrorType,
  BiometryType,
} from '@aparajita/capacitor-biometric-auth'

const FACE_ID_KEY = 'face_id_enabled'
let cachedFaceIdEnabled: boolean | null = null
let cachedBiometryName: string | null = null
let cachedBiometryType: BiometryType | null = null

export { BiometryType }

function biometryNameFor(type: BiometryType): string {
  switch (type) {
    case BiometryType.touchId:
      return 'Touch ID'
    case BiometryType.fingerprintAuthentication:
      return 'Fingerprint'
    default:
      return 'Face ID'
  }
}

export async function getBiometryName(): Promise<string> {
  if (!Capacitor.isNativePlatform()) return 'Face ID'
  if (cachedBiometryName !== null) return cachedBiometryName
  try {
    const { biometryType } = await BiometricAuth.checkBiometry()
    cachedBiometryType = biometryType
    cachedBiometryName = biometryNameFor(biometryType)
  } catch {
    cachedBiometryName = 'Face ID'
  }
  return cachedBiometryName
}

export function isFingerprintBiometry(type: BiometryType | null): boolean {
  return type === BiometryType.touchId || type === BiometryType.fingerprintAuthentication
}

export function useBiometryType(): BiometryType | null {
  const [type, setType] = useState<BiometryType | null>(cachedBiometryType)

  useEffect(() => {
    if (cachedBiometryType !== null || !Capacitor.isNativePlatform()) return
    let cancelled = false
    void getBiometryName().then(() => {
      if (!cancelled) setType(cachedBiometryType)
    })
    return () => {
      cancelled = true
    }
  }, [])

  return type
}

export function useBiometryName(): string {
  const [name, setName] = useState(cachedBiometryName ?? 'Face ID')

  useEffect(() => {
    let cancelled = false
    void getBiometryName().then((value) => {
      if (!cancelled) setName(value)
    })
    return () => {
      cancelled = true
    }
  }, [])

  return name
}

export async function getFaceIdEnabled(): Promise<boolean> {
  if (!Capacitor.isNativePlatform()) {
    cachedFaceIdEnabled = false
    return false
  }
  const { value } = await Preferences.get({ key: FACE_ID_KEY })
  const enabled = value === 'true'
  cachedFaceIdEnabled = enabled
  return enabled
}

export function getCachedFaceIdEnabled(): boolean | null {
  return cachedFaceIdEnabled
}

async function persistFaceIdEnabled(enabled: boolean) {
  cachedFaceIdEnabled = enabled
  if (enabled) {
    await Preferences.set({ key: FACE_ID_KEY, value: 'true' })
  } else {
    await Preferences.remove({ key: FACE_ID_KEY })
  }
}

// The biometric sheet resigns/reactivates the app, so BiometricLockProvider
// must know a prompt is up (from settings or the lock screen) to keep its
// appStateChange handler inert — otherwise the sheet's own state changes
// arm/re-trigger the lock.
let verifyInFlight = false

export function isVerifyInFlight(): boolean {
  return verifyInFlight
}

export async function verifyFaceId(reason: string): Promise<void> {
  verifyInFlight = true
  try {
    await BiometricAuth.authenticate({
      reason,
      cancelTitle: 'Cancel',
      allowDeviceCredential: false,
      // Empty string hides the iOS fallback button — Face ID only, no passcode.
      iosFallbackTitle: '',
      // Android's BiometricPrompt shows a title above the reason text.
      androidTitle: reason,
    })
  } finally {
    verifyInFlight = false
  }
}

// While biometry is locked out (too many failures) checkBiometry reports
// isAvailable: false; it flips back to true once the user passcode-unlocks
// the device.
export async function isBiometryUsable(): Promise<boolean> {
  try {
    const { isAvailable } = await BiometricAuth.checkBiometry()
    return isAvailable
  } catch {
    return false
  }
}

export function isFaceIdCancel(error: unknown): boolean {
  return (
    error instanceof BiometryError &&
    (error.code === BiometryErrorType.userCancel ||
      error.code === BiometryErrorType.systemCancel ||
      error.code === BiometryErrorType.appCancel)
  )
}

export function isFaceIdLockout(error: unknown): boolean {
  return error instanceof BiometryError && error.code === BiometryErrorType.biometryLockout
}

export function useFaceIdEnabled() {
  const [enabled, setEnabled] = useState(false)
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    let cancelled = false
    getFaceIdEnabled().then((value) => {
      if (!cancelled) {
        setEnabled(value)
        setIsLoading(false)
      }
    })
    return () => {
      cancelled = true
    }
  }, [])

  const setEnabledChecked = useCallback(async (next: boolean) => {
    if (!Capacitor.isNativePlatform()) {
      throw new Error('Biometric unlock is only available in the mobile app')
    }
    if (next) {
      const { isAvailable, reason, biometryType } = await BiometricAuth.checkBiometry()
      const name = biometryNameFor(biometryType)
      cachedBiometryType = biometryType
      cachedBiometryName = name
      if (!isAvailable) {
        throw new Error(reason || `${name} isn't set up on this device`)
      }
      await verifyFaceId(`Confirm ${name} to enable app unlock`)
    } else {
      const name = await getBiometryName()
      await verifyFaceId(`Confirm ${name} to disable app unlock`)
    }
    await persistFaceIdEnabled(next)
    setEnabled(next)
  }, [])

  return { enabled, setEnabled: setEnabledChecked, isLoading }
}
